import { loadCss } from '../assets/css';
import { upsertHiddenInput } from '../dom/create';
import { loadScript } from '../dom/script';
import requestIdleCallback from '../shims/requestIdleCallback';
import { yieldToMainThread } from '../yield';

class ViewModeSwitcher {
    _btns: HTMLCollectionOf<HTMLButtonElement>;
    _mapContainer: HTMLElement | null;
    _gridContainer: HTMLElement;

    constructor() {
        this._btns = document.getElementsByClassName( 'twc__grid--switcher-btn' ) as HTMLCollectionOf<HTMLButtonElement>;
        this._mapContainer = document.querySelector<HTMLElement>( '#map-search-container' );
        this._gridContainer = document.querySelector<HTMLElement>( '#grid-search-container' ) as HTMLElement;

        for ( let i = 0; i < this._btns.length; i++ ) {
            this._btns[ i ].addEventListener( 'click', ( event: PointerEvent ) => {
                if ( !event.target || !( event.target instanceof HTMLElement ) ) {
                    return;
                }

                if ( !event.target.dataset.targetMode ) {
                    return;
                }

                if ( event.target.dataset.targetMode === 'map' ) {
                    this.onMapClick( event );
                } else {
                    this.onListClick( event );
                }
            } );
        }
    }

    onListClick( event: Event ): void {
        const form = document.querySelector<HTMLFormElement>( '#property-search-form-base' );

        if ( !form ) {
            throw new Error( 'failed to find property search form' );
        }

        upsertHiddenInput( form, 'destination', '' );
        form.submit();
    }

    async onMapClick( event: Event ): Promise<void> {
        for ( let i = 0; i < this._btns.length; i++ ) {
            this._btns[ i ].dataset.targetMode = 'list';
            this._btns[ i ].innerHTML = this._btns[ i ].dataset.contentsList as string;
        }
        await this.showMap();
    }

    showMap(): Promise<void> {
        if ( !this._mapContainer ) {
            document.body.insertAdjacentHTML( 'beforeend', `
                <section id="map-search-container">
                    <div id="twc__map-search-container">
                        <div class="loader"><div class="spinner"></div></div>
                        <div id="twc__map-search-map">
                            <div class="twc__map-search-control-container">
                                <div class="twc__map-search-control-container-bottom"></div>
                            </div>
                        </div>
                    </div>
                </section>
            ` );

            this._mapContainer = document.querySelector( '#map-search-container' ) as HTMLElement;
        }

        ( this._mapContainer as HTMLElement ).dispatchEvent( new CustomEvent( 'twc:mapsearch:initialising', { bubbles: true } ) );

        document.dispatchEvent( new CustomEvent( 'twc:collapse:collapse-all', {
            bubbles: true,
        } ) );

        const show = () => {
            document.documentElement.classList.add( 'mapsearch' );
            this._gridContainer.classList.add( 'd-none' );

            requestIdleCallback( () => {
                this._gridContainer.remove();
            } );
        };

        const loadMapService = async function() {
            loadCss( window.assetsPrefix + '/css/property-map-search.css' );

            const library = loadScript( 'https://api.mapbox.com/mapbox-gl-js/v1.13.2/mapbox-gl.js' );
            const vendors = loadScript( window.assetsPrefix + '/js/vendors-map-search.js' );
            const svc = loadScript( window.assetsPrefix + '/js/map-search-service.js' );

            await Promise.all( [
                library,
                vendors,
                svc,
            ] );

            while ( typeof window.mapSearchService === 'undefined' ) {
                await yieldToMainThread();
            }

            await window.mapSearchService.init();
        };

        return new Promise( async function( resolve: ( v?: any ) => void, reject: ( v?: any ) => void ) {
            if ( typeof window.mapSearchService === 'undefined' ) {
                show();

                await yieldToMainThread();

                window.addEventListener( 'twc:mapsearch:initialised', resolve );

                loadMapService()
                    .catch( function( error ) {
                        reject( error );
                    } );
            } else {
                show();
                resolve();
            }
        }.bind( this ) );
    }
}

export default ViewModeSwitcher;
